import { LayoutGrid, LayoutList, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type ViewMode = 'table' | 'card';

interface ViewToggleProps {
  view: ViewMode;
  onViewChange: (view: ViewMode) => void;
  onRefresh?: () => void;
  className?: string;
}

export function ViewToggle({ view, onViewChange, onRefresh, className }: ViewToggleProps) {
  return (
    <div className={cn('flex items-center gap-1 border border-border rounded-lg p-1', className)}>
      {onRefresh && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 hover:bg-accent/50"
          onClick={onRefresh}
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      )}
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          'h-8 w-8 hover:bg-accent/50',
          view === 'table' && 'bg-accent'
        )}
        onClick={() => onViewChange('table')}
      >
        <LayoutList className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className={cn(
          'h-8 w-8 hover:bg-accent/50',
          view === 'card' && 'bg-accent'
        )}
        onClick={() => onViewChange('card')}
      >
        <LayoutGrid className="h-4 w-4" />
      </Button>
    </div>
  );
}

// Hook for persisting view preference
import { useState, useEffect } from 'react';

export function useViewPreference(key: string, defaultView: ViewMode = 'table'): [ViewMode, (view: ViewMode) => void] {
  const [view, setView] = useState<ViewMode>(() => {
    if (typeof window === 'undefined') return defaultView;
    const stored = localStorage.getItem(`view-preference-${key}`);
    return (stored as ViewMode) || defaultView;
  });

  useEffect(() => {
    localStorage.setItem(`view-preference-${key}`, view);
  }, [key, view]);

  return [view, setView];
}
